import { computed, Injectable, signal } from '@angular/core';
import { ApiGatewayService, OrganizationSubscription, PriceListProduct } from './api-gateway.service';

export type OrderProduct = 'KIT' | 'CONNECTIVITY_SINGLE';
export type PaymentTerm = 'annual' | 'monthly';

export const orderPages: Record<OrderProduct, string> = {
  KIT: 'subscription/kit',
  CONNECTIVITY_SINGLE: 'subscription/connectivity',
};

export const fleetPage = 'subscription/fleet';

export interface OrderLine {
  type: OrderProduct,
  quantity: number,
  unitCents?: number,
  totalCents?: number,
  nodes: number
}

export interface FleetLine {
  name: string,
  quantity: number,
  unitCents?: number,
  totalCents?: number,
  monthly: boolean
}

export interface FleetPlan {
  plan: PriceListProduct,
  block?: PriceListProduct,
  blocks: number,
  capacity: number,
  cost: number
}

type Price = PriceListProduct['prices'][number];

const pounds = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 });
const pence = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 });

export function formatMoney(cents?: number): string {
  return cents === undefined ? 'On request' : (cents % 100 === 0 ? pounds : pence).format(cents / 100);
}

export function activeSubscriptions(subscriptions: OrganizationSubscription[]): OrganizationSubscription[] {
  const now = Date.now();
  return subscriptions.filter(s => new Date(s.starts_at).getTime() <= now && now < new Date(s.ends_at).getTime());
}

function band(product: PriceListProduct | undefined, quantity: number): Price | undefined {
  return product?.prices.find(p =>
    p.currency === 'GBP'
    && p.band_min_quantity <= quantity
    && (p.band_max_quantity == null || quantity <= p.band_max_quantity));
}

function unitPriceCents(product: PriceListProduct | undefined, quantity: number): number | undefined {
  return band(product, quantity)?.unit_price_cents || undefined;
}

export function nextBand(product: PriceListProduct | undefined, quantity: number): Price | undefined {
  return product?.prices
    .filter(p => p.currency === 'GBP' && p.band_min_quantity > quantity)
    .sort((a, b) => a.band_min_quantity - b.band_min_quantity)[0];
}

function fleetLine(product: PriceListProduct, quantity: number): FleetLine {
  const price = band(product, quantity);
  const unitCents = price?.unit_price_cents || undefined;
  return { name: product.name, quantity, unitCents, totalCents: unitCents && unitCents * quantity, monthly: !!unitCents && !!price?.allows_monthly };
}

function fleetCandidate(products: PriceListProduct[], plan: PriceListProduct, target: number): FleetPlan | undefined {
  const planCents = unitPriceCents(plan, 1);
  if (planCents === undefined) {
    return undefined;
  }
  const candidate = { plan, blocks: 0, capacity: plan.amount, cost: planCents };
  const block = products.find(p => p.type === 'CONNECTIVITY_FLEET' && p.required_product_uuid === plan.uuid);
  if (target <= plan.amount || !block || block.amount <= 0) {
    return candidate;
  }
  const blocks = Math.ceil((target - plan.amount) / block.amount);
  const blockCents = unitPriceCents(block, blocks);
  if (blockCents === undefined) {
    return candidate;
  }
  return { plan, block, blocks, capacity: plan.amount + blocks * block.amount, cost: planCents + blockCents * blocks };
}

function recommendFleet(products: PriceListProduct[], target: number): FleetPlan | undefined {
  return products
    .filter(p => p.type === 'CONNECTIVITY_FLEET' && !p.required_product_uuid)
    .map(plan => fleetCandidate(products, plan, target))
    .filter((c): c is FleetPlan => c !== undefined && c.capacity >= target)
    .sort((a, b) => (a.cost - b.cost) || (a.blocks - b.blocks))[0];
}

function instalmentCents(yearlyCents: number): number {
  return Math.trunc(yearlyCents / 10) + (yearlyCents % 10 >= 5 ? 1 : 0);
}

function sum(lines: { totalCents?: number }[]): number {
  return lines.reduce((total, line) => total + (line.totalCents ?? 0), 0);
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionOrderService {
  readonly quantity = signal<Record<OrderProduct, number>>({ KIT: 0, CONNECTIVITY_SINGLE: 0 });
  readonly fleetSize = signal(0);
  readonly nodeQuantity = signal(0);
  readonly paymentTerm = signal<PaymentTerm>('annual');
  readonly products = signal<PriceListProduct[]>([]);

  readonly lines = computed<OrderLine[]>(() => (['KIT', 'CONNECTIVITY_SINGLE'] as const).flatMap(type => {
    const product = this.product(type);
    const quantity = this.quantity()[type];
    if (!product || !quantity) {
      return [];
    }
    const unitCents = unitPriceCents(product, quantity);
    const required = this.products().find(p => p.uuid === product.required_product_uuid);
    return [{
      type,
      quantity,
      unitCents,
      totalCents: unitCents && unitCents * quantity,
      nodes: quantity * (required ?? product).amount,
    }];
  }));

  readonly totalCents = computed(() => sum(this.lines()));
  readonly nodes = computed(() => this.lines().reduce((total, line) => total + line.nodes, 0));

  readonly node = computed(() => this.product('NODE'));
  readonly fleetPlan = computed(() => this.fleetSize() ? recommendFleet(this.products(), this.fleetSize()) : undefined);
  readonly planLines = computed<FleetLine[]>(() => {
    const plan = this.fleetPlan();
    if (plan) {
      return [fleetLine(plan.plan, 1), ...(plan.block ? [fleetLine(plan.block, plan.blocks)] : [])];
    }
    return this.fleetSize() ? [{ name: `Fleet plan for ${this.fleetSize().toLocaleString('en-GB')} nodes`, quantity: 1, monthly: false }] : [];
  });
  readonly nodeLine = computed(() => {
    const node = this.node();
    return node && this.nodeQuantity() ? fleetLine(node, this.nodeQuantity()) : undefined;
  });
  readonly planCents = computed(() => this.planLines().some(line => line.totalCents === undefined) ? undefined : sum(this.planLines()));
  readonly fleetLines = computed(() => [...this.planLines(), ...(this.nodeLine() ? [this.nodeLine()!] : [])]);
  readonly monthlyAllowed = computed(() => this.fleetLines().some(line => line.monthly));
  readonly monthly = computed(() => this.monthlyAllowed() && this.paymentTerm() === 'monthly');
  readonly instalmentCents = computed(() => instalmentCents(sum(this.fleetLines().filter(line => line.monthly))));
  readonly dueTodayCents = computed(() => this.monthly()
    ? sum(this.fleetLines().filter(line => !line.monthly)) + this.instalmentCents()
    : sum(this.fleetLines()));

  constructor(private gatewayApiService: ApiGatewayService) {}

  async loadPriceList() {
    this.products.set((await this.gatewayApiService.getPriceList()).body ?? []);
  }

  offered(type: string): boolean {
    return !!this.product(type)?.prices.length;
  }

  unitCents(type: OrderProduct): number | undefined {
    return unitPriceCents(this.product(type), Math.max(1, this.quantity()[type]));
  }

  set(type: OrderProduct, quantity: number) {
    this.quantity.update(current => ({ ...current, [type]: quantity }));
  }

  clear() {
    this.quantity.set({ KIT: 0, CONNECTIVITY_SINGLE: 0 });
  }

  private product(type: string): PriceListProduct | undefined {
    return this.products().find(p => p.type === type);
  }
}
